import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { ChevronRight } from 'lucide-react';

import styles from './AthleteTrainingResults.module.css';

import {
  getAthleteTrainingMeetSummaries,
  getTrainingResults,
} from 'api/resultApi';
import { convertMillisecondsToRecord, convertDateTime } from 'utils/time';

const BADGE_CLASSES = {
  'Sprint Training': styles.badgeSprint,
  'Flying Start': styles.badgeFlying,
  'Agility Training': styles.badgeAgility,
  'Shuttle Run': styles.badgeShuttle,
  'Lap Training': styles.badgeLap,
  '40 Yard Dash': styles.badgeYards,
};

const isYardsEvent = (eventType) => eventType === '40 Yard Dash';
const metersToYards = (m) => Math.round(m / 0.9144);
// 1 m/s = 2.2369362920544 mph
const msToMph = (v) => v * 2.2369362920544;

const getSplitsLabel = (summary) => {
  if (isYardsEvent(summary.eventType)) {
    const yardDistances = [...summary.splitDistances, summary.finishDistance]
      .map((d) => `${metersToYards(d)} yd`)
      .join(' \u2192 ');
    return `Splits: ${yardDistances}`;
  }

  const distances = [...summary.splitDistances, summary.finishDistance]
    .map((d) => `${d}m`)
    .join(' \u2192 ');

  switch (summary.eventType) {
    case 'Sprint Training':
    case 'Agility Training':
      return `Splits: ${distances}`;
    case 'Flying Start':
      return `Start: ${summary.startDistance}m \u2192 ${distances}`;
    case 'Shuttle Run':
      return `Distance: ${summary.finishDistance}m`;
    case 'Lap Training': {
      const lapLabel = `Lap: ${summary.finishDistance}m`;
      if (summary.splitDistances.length === 0) return lapLabel;
      const splitsOnly = summary.splitDistances
        .map((d) => `${d}m`)
        .join(' \u2192 ');
      return `${lapLabel}. Splits: ${splitsOnly}`;
    }
    default:
      return `Splits: ${distances}`;
  }
};

const AthleteTrainingResults = ({
  clubId,
  athleteId,
  seasonId = null,
  onStatsLoaded,
}) => {
  const [loading, setLoading] = useState(true);
  const [meetSummaries, setMeetSummaries] = useState([]);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [loadingEvent, setLoadingEvent] = useState(null);
  const [showVelocity, setShowVelocity] = useState(false);

  const formatTime = (ms) => {
    if (ms == null) return '\u2014';
    return convertMillisecondsToRecord(ms, 2);
  };

  const formatReaction = (ms) => {
    if (ms == null) return '\u2014';
    return (ms / 1000).toFixed(3);
  };

  const formatVelocity = (v, eventType) => {
    if (v == null) return null;
    const value = isYardsEvent(eventType) ? msToMph(v) : v;
    return (Math.round(value * 10 + Number.EPSILON) / 10).toFixed(1);
  };

  const fetchSummaries = useCallback(async () => {
    try {
      const data = await getAthleteTrainingMeetSummaries(
        clubId,
        athleteId,
        seasonId
      );
      const meets = data?.meets || [];
      setMeetSummaries(meets);

      // Report stats to parent
      if (onStatsLoaded) {
        const totalMeets = meets.length;
        const totalEvents = meets.reduce(
          (sum, m) => sum + m.eventSummaries.length,
          0
        );
        const totalRuns = meets.reduce(
          (sum, m) =>
            sum + m.eventSummaries.reduce((s, e) => s + e.totalRuns, 0),
          0
        );
        onStatsLoaded({
          numberOfSessions: totalMeets,
          numberOfEvents: totalEvents,
          numberOfRuns: totalRuns,
        });
      }
    } catch (err) {
      console.error('[AthleteTrainingResults] Fetch summaries error:', err);
      toast.error('Failed to load training results');
    } finally {
      setLoading(false);
    }
  }, [clubId, athleteId, seasonId, onStatsLoaded]);

  useEffect(() => {
    setLoading(true);
    setExpandedEvents({});
    fetchSummaries();
  }, [fetchSummaries]);

  const getEventKey = (meetId, summary) =>
    `${meetId}-${summary.eventType}-${summary.splitConfig}`;

  const toggleEvent = async (meet, summary) => {
    const key = getEventKey(meet.meetId, summary);

    if (expandedEvents[key]) {
      setExpandedEvents((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }

    setLoadingEvent(key);
    try {
      const data = await getTrainingResults(
        clubId,
        meet.meetId,
        summary.eventIds,
        athleteId
      );
      const athlete = data?.athletes?.[0] || null;
      setExpandedEvents((prev) => ({
        ...prev,
        [key]: { ...data, athlete },
      }));
    } catch (err) {
      console.error('[AthleteTrainingResults] Fetch results error:', err);
      toast.error('Failed to load event results');
    } finally {
      setLoadingEvent(null);
    }
  };

  if (loading) {
    return <div className={styles.loadingMessage}>Loading training results...</div>;
  }

  if (meetSummaries.length === 0) {
    return (
      <div className={styles.emptyState}>No training results found.</div>
    );
  }

  return (
    <div>
      {/* Velocity toggle */}
      <div className={styles.filtersBar}>
        <label className={styles.velocityToggle}>
          <input
            type="checkbox"
            checked={showVelocity}
            onChange={(e) => setShowVelocity(e.target.checked)}
          />
          Include Velocity (m/s · mph for 40 Yard Dash)
        </label>
      </div>

      {meetSummaries.map((meet) => (
        <div key={meet.meetId} className={styles.meetSection}>
          <div className={styles.meetHeading}>
            <h3 className={styles.meetName}>{meet.meetName}</h3>
            <span className={styles.meetDate}>
              {convertDateTime(meet.meetDate).date}
            </span>
          </div>

          <div className={styles.resultsPanel}>
            <table className={styles.resultsTable}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <th style={{ minWidth: 160 }}>Event</th>
                  <th style={{ width: '100%' }}>Config</th>
                  <th className={styles.thNumeric}>
                    Runs
                  </th>
                  <th className={styles.thNumeric}>Best</th>
                </tr>
              </thead>
              <tbody>
                {meet.eventSummaries.map((summary) => {
                  const key = getEventKey(meet.meetId, summary);
                  const expanded = expandedEvents[key];
                  const isLoading = loadingEvent === key;
                  const isExpanded = !!expanded;

                  return (
                    <React.Fragment key={key}>
                      {/* Summary Row */}
                      <tr
                        className={`${styles.eventRow} ${isExpanded ? styles.eventRowExpanded : ''}`}
                        onClick={() => toggleEvent(meet, summary)}
                      >
                        <td>
                          <span
                            className={`${styles.expandIcon} ${isExpanded ? styles.expandIconOpen : ''}`}
                          >
                            {isLoading ? (
                              <span className={styles.spinner} />
                            ) : (
                              <ChevronRight size={14} />
                            )}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`${styles.eventTypeBadge} ${BADGE_CLASSES[summary.eventType] || styles.badgeSprint}`}
                          >
                            {summary.eventType}
                          </span>
                        </td>
                        <td className={styles.configCell}>
                          {getSplitsLabel(summary)}
                        </td>
                        <td className={styles.numeric}>
                          <span className={styles.runCount}>
                            {summary.totalRuns}
                          </span>
                        </td>
                        <td className={`${styles.numeric} ${styles.best}`}>
                          {formatTime(summary.bestFinishMs)}
                        </td>
                      </tr>

                      {/* Detail Row */}
                      {isExpanded && expanded.athlete && (
                        <tr className={styles.detailContainer}>
                          <td colSpan={5}>
                            <div className={styles.detailInner}>
                              <table className={styles.detailTable}>
                                <tbody>
                                  <tr className={styles.detailHeaderRow}>
                                    <td
                                      style={{
                                        minWidth: expanded.isLapTraining
                                          ? 120
                                          : 60,
                                        padding: '10px 12px',
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: '#8b95a5',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.4px',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {expanded.isLapTraining
                                        ? `${expanded.athlete.runs.length} ${expanded.athlete.runs.length === 1 ? 'Lap' : 'Laps'} \u00B7 ${expanded.athlete.runs.length * expanded.finishDistance}m \u00B7 ${formatTime(expanded.athlete.runs.reduce((sum, r) => sum + (r.finishMs || 0), 0))}`
                                        : 'Run'}
                                    </td>
                                    {expanded.hasReaction && (
                                      <td
                                        style={{
                                          padding: '10px 12px',
                                          fontSize: 11,
                                          fontWeight: 600,
                                          color: '#8b95a5',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.4px',
                                          textAlign: 'right',
                                        }}
                                      >
                                        Reaction
                                      </td>
                                    )}
                                    {expanded.hasStart && (
                                      <td
                                        style={{
                                          padding: '10px 12px',
                                          fontSize: 11,
                                          fontWeight: 600,
                                          color: '#8b95a5',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.4px',
                                          textAlign: 'right',
                                        }}
                                      >
                                        {isYardsEvent(expanded.eventType)
                                          ? `Start ${metersToYards(expanded.startDistance)}yd`
                                          : `Start ${expanded.startDistance}m`}
                                      </td>
                                    )}
                                    {expanded.splitDistances.map((dist) => (
                                      <td
                                        key={dist}
                                        style={{
                                          padding: '10px 12px',
                                          fontSize: 11,
                                          fontWeight: 600,
                                          color: '#8b95a5',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.4px',
                                          textAlign: 'right',
                                        }}
                                      >
                                        {isYardsEvent(expanded.eventType)
                                          ? `${metersToYards(dist)}yd`
                                          : `${dist}m`}
                                      </td>
                                    ))}
                                    <td
                                      style={{
                                        padding: '10px 12px',
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: '#8b95a5',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.4px',
                                        textAlign: 'right',
                                      }}
                                    >
                                      {isYardsEvent(expanded.eventType)
                                        ? `${metersToYards(expanded.finishDistance)}yd`
                                        : `${expanded.finishDistance}m`}
                                    </td>
                                  </tr>
                                  {expanded.athlete.runs.map((run) => (
                                    <tr key={run.runNumber}>
                                      <td>
                                        <span className={styles.runLabel}>
                                          {expanded.isLapTraining
                                            ? `Lap ${run.runNumber}`
                                            : `Run ${run.runNumber}`}
                                        </span>
                                      </td>
                                      {expanded.hasReaction && (
                                        <td
                                          className={`${styles.detailNumeric} ${run.isReactionBest ? styles.isBest : ''}`}
                                        >
                                          {formatReaction(run.reactionMs)}
                                        </td>
                                      )}
                                      {expanded.hasStart && (
                                        <td className={styles.detailNumeric}>
                                          0.00
                                        </td>
                                      )}
                                      {expanded.splitDistances.map((dist) => {
                                        const split = run.splits?.find(
                                          (s) => s.distance === dist
                                        );
                                        return (
                                          <td
                                            key={dist}
                                            className={`${styles.detailNumeric} ${split?.isBest ? styles.isBest : ''}`}
                                          >
                                            {showVelocity &&
                                              split?.velocity != null && (
                                                <span
                                                  className={
                                                    styles.velocityValue
                                                  }
                                                >
                                                  ({formatVelocity(
                                                    split.velocity,
                                                    expanded.eventType
                                                  )})
                                                </span>
                                              )}
                                            {formatTime(
                                              split?.timeInMilliseconds
                                            )}
                                          </td>
                                        );
                                      })}
                                      <td
                                        className={`${styles.detailNumeric} ${run.isFinishBest ? styles.isBest : ''}`}
                                      >
                                        {showVelocity &&
                                          run.finishVelocity != null && (
                                            <span
                                              className={styles.velocityValue}
                                            >
                                              ({formatVelocity(
                                                run.finishVelocity,
                                                expanded.eventType
                                              )})
                                            </span>
                                          )}
                                        {formatTime(run.finishMs)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

AthleteTrainingResults.propTypes = {
  clubId: PropTypes.string.isRequired,
  athleteId: PropTypes.string.isRequired,
  seasonId: PropTypes.string,
  onStatsLoaded: PropTypes.func,
};

export default AthleteTrainingResults;
